<template>
  <section class="scan-suite">
    <div class="scan-suite__hero">
      <div>
        <span class="scan-suite__eyebrow">Scan mode</span>
        <h2>Escaneo en vivo para ingreso rapido.</h2>
        <p>Usa el payload estable del ticket para validar acceso, dejar trazabilidad y detectar al instante si una entrada ya fue usada.</p>
      </div>

      <div class="scan-suite__pills">
        <span>{{ event.name }}</span>
        <span>{{ paused ? 'Camara pausada' : 'Camara activa' }}</span>
      </div>
    </div>

    <div class="scan-suite__layout">
      <article class="scan-suite__camera-card">
        <div v-if="!success && !loading && !err && !paused" class="scan-suite__camera-shell">
          <qr-stream @init="onInit" @decode="onDecode" class="scan-suite__camera"></qr-stream>
          <div class="scan-suite__overlay">
            <div class="scan-suite__frame">
              <div class="scan-suite__corner scan-suite__corner--tl"></div>
              <div class="scan-suite__corner scan-suite__corner--tr"></div>
              <div class="scan-suite__corner scan-suite__corner--bl"></div>
              <div class="scan-suite__corner scan-suite__corner--br"></div>
              <div class="scan-suite__beam"></div>
            </div>
          </div>
        </div>

        <div v-else-if="loading" class="scan-suite__state">
          <spring-spinner :size="64" color="#9cb8ff" />
          <p>Validando payload y comprobando la entrada...</p>
        </div>

        <div v-else-if="success" class="scan-suite__result scan-suite__result--success">
          <span class="scan-suite__result-kicker">Acceso concedido</span>
          <h3>{{ ticketData.firstName }} {{ ticketData.lastName }}</h3>
          <p>{{ ticketData.productName }}</p>
          <div class="scan-suite__result-grid">
            <span>{{ ticketData.seatName || 'Acceso libre' }}</span>
            <span>{{ ticketData.ticketCode }}</span>
            <span>{{ ticketData.scannedAtLabel }}</span>
          </div>
          <button class="btn btn-primary" type="button" @click="restartSearch">Escanear otra entrada</button>
        </div>

        <div v-else-if="err" class="scan-suite__result scan-suite__result--error">
          <span class="scan-suite__result-kicker">Acceso bloqueado</span>
          <h3>{{ errorTitle }}</h3>
          <p>{{ errorMessage }}</p>
          <div v-if="lastScanInfo" class="scan-suite__result-grid">
            <span>{{ lastScanInfo }}</span>
          </div>
          <button class="btn btn-outline-light" type="button" @click="restartSearch">Reintentar</button>
        </div>

        <div v-else class="scan-suite__state scan-suite__state--paused">
          <p>Camara en pausa. Reanuda cuando quieras volver a escanear.</p>
        </div>
      </article>

      <aside class="scan-suite__side-card">
        <div class="scan-suite__side-block">
          <span class="scan-suite__side-kicker">Ultimo payload</span>
          <code>{{ result || 'Sin lectura todavia' }}</code>
        </div>

        <div class="scan-suite__controls">
          <button v-if="!paused && !loading" class="btn btn-outline-light" type="button" @click="pauseScan">Pausar</button>
          <button v-if="paused" class="btn btn-outline-light" type="button" @click="resumeScan">Reanudar</button>
          <button class="btn btn-primary" type="button" @click="restartSearch">Reiniciar</button>
        </div>

        <div class="scan-suite__side-block">
          <span class="scan-suite__side-kicker">Trazabilidad</span>
          <p>Cuando un acceso se procesa, guardamos quien escaneo y la hora exacta para auditoria operativa.</p>
        </div>
      </aside>
    </div>
  </section>
</template>

<script>
import { QrStream } from 'vue3-qr-reader'
import { SpringSpinner } from 'epic-spinners'
import { apiService } from '../../common/api.services.js'

export default {
  components: { QrStream, SpringSpinner },

  props: {
    event: {
      required: true,
    },
  },

  data() {
    return {
      result: '',
      errorTitle: 'No pudimos validar esta entrada',
      errorMessage: '',
      lastScanInfo: '',
      loading: false,
      success: false,
      err: false,
      paused: false,
      ticketData: {
        firstName: '',
        lastName: '',
        seatName: '',
        productName: '',
        ticketCode: '',
        scannedAtLabel: '',
      },
      isDecoding: false,
    }
  },

  methods: {
    pauseScan() {
      this.paused = true
    },

    resumeScan() {
      this.paused = false
    },

    restartSearch() {
      this.result = ''
      this.errorTitle = 'No pudimos validar esta entrada'
      this.errorMessage = ''
      this.lastScanInfo = ''
      this.loading = false
      this.success = false
      this.err = false
      this.paused = false
      this.isDecoding = false
      this.ticketData = {
        firstName: '',
        lastName: '',
        seatName: '',
        productName: '',
        ticketCode: '',
        scannedAtLabel: '',
      }
    },

    async onDecode(payload) {
      if (this.isDecoding) {
        return
      }

      this.isDecoding = true
      this.result = payload
      this.loading = true
      this.success = false
      this.err = false
      this.errorMessage = ''
      this.lastScanInfo = ''

      try {
        const data = await apiService(`/api/scan-invoice-item/${this.event.id}/`, 'POST', { payload })

        if (data && data.message === 'Entrada Procesada' && data.ticket) {
          this.success = true
          this.ticketData = {
            firstName: data.ticket.first_name,
            lastName: data.ticket.last_name,
            seatName: data.ticket.seat_name,
            productName: data.ticket.product_name,
            ticketCode: data.ticket.access_code_snapshot || 'Sin codigo',
            scannedAtLabel: this.formatScanDate(data.ticket.scanned_at),
          }
          return
        }

        this.handleApiError(data)
      } catch (error) {
        this.handleError('No pudimos procesar la validacion de la entrada.')
      } finally {
        this.loading = false
        this.isDecoding = false
      }
    },

    handleApiError(data) {
      if (data && data.err) {
        this.errorTitle = data.err === 'Entrada en uso' ? 'Esta entrada ya fue utilizada' : 'Acceso no permitido'
        this.errorMessage = data.err
        if (data.scanned_by_name || data.scanned_at) {
          this.lastScanInfo = `Ultimo registro: ${data.scanned_by_name || 'usuario no identificado'} · ${this.formatScanDate(data.scanned_at)}`
        }
        this.err = true
        return
      }

      this.handleError('No se encontro una entrada valida para este QR.')
    },

    async onInit(promise) {
      try {
        await promise
      } catch (error) {
        this.handleError(this.getCameraError(error.name))
      }
    },

    handleError(message) {
      this.errorMessage = message
      this.err = true
      this.loading = false
    },

    formatScanDate(value) {
      if (!value) {
        return 'Sin hora registrada'
      }

      return new Intl.DateTimeFormat('default', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value))
    },

    getCameraError(errorName) {
      const errors = {
        NotAllowedError: 'Debes otorgar permiso para acceder a la camara.',
        NotFoundError: 'No se encontro una camara en este dispositivo.',
        NotSupportedError: 'Se requiere HTTPS o localhost para usar la camara.',
        NotReadableError: 'La camara ya esta en uso por otra aplicacion.',
        OverconstrainedError: 'Las camaras instaladas no cumplen con el requisito solicitado.',
        StreamApiNotSupportedError: 'Este navegador no soporta la API de camara.',
        InsecureContextError: 'El acceso a la camara solo funciona en contexto seguro.',
      }

      return errors[errorName] || `Error de camara: ${errorName}`
    },
  },
}
</script>

<style scoped>
.scan-suite {
  display: grid;
  gap: 18px;
}

.scan-suite__hero,
.scan-suite__camera-card,
.scan-suite__side-card {
  padding: 20px;
  border-radius: 26px;
  background:
    radial-gradient(circle at top left, rgba(214, 221, 232, 0.07), transparent 28%),
    radial-gradient(circle at top right, rgba(98, 76, 144, 0.16), transparent 24%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.01)),
    rgba(9, 10, 15, 0.96);
  border: 1px solid rgba(201, 211, 231, 0.08);
}

.scan-suite__hero,
.scan-suite__layout {
  display: grid;
  gap: 18px;
}

.scan-suite__layout {
  grid-template-columns: minmax(0, 1.2fr) 320px;
}

.scan-suite__eyebrow,
.scan-suite__side-kicker,
.scan-suite__result-kicker {
  color: #cfd8ea;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.scan-suite__hero h2,
.scan-suite__result h3 {
  margin: 8px 0 6px;
  color: #fff;
}

.scan-suite__hero p,
.scan-suite__result p,
.scan-suite__side-block p,
.scan-suite__result-grid span,
.scan-suite__side-block code,
.scan-suite__state p {
  color: rgba(194, 204, 223, 0.82);
}

.scan-suite__pills,
.scan-suite__result-grid,
.scan-suite__controls {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.scan-suite__pills span,
.scan-suite__result-grid span,
.scan-suite__side-block code {
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
}

.scan-suite__camera-shell {
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  min-height: 420px;
}

.scan-suite__camera {
  width: 100%;
  min-height: 420px;
}

.scan-suite__overlay {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(6, 8, 13, 0.36);
  pointer-events: none;
}

.scan-suite__frame {
  position: relative;
  width: min(62vw, 280px);
  height: min(62vw, 280px);
  border-radius: 28px;
  border: 1px solid rgba(188, 223, 255, 0.18);
  box-shadow:
    0 0 0 1px rgba(125, 164, 255, 0.08),
    0 28px 52px rgba(0, 0, 0, 0.26);
}

.scan-suite__beam {
  position: absolute;
  inset: 18px 14px auto;
  height: 3px;
  border-radius: 999px;
  background: linear-gradient(90deg, transparent, rgba(157, 210, 255, 0.9), transparent);
  box-shadow: 0 0 18px rgba(157, 210, 255, 0.7);
  animation: scan-beam 2.4s ease-in-out infinite;
}

.scan-suite__corner {
  position: absolute;
  width: 26px;
  height: 26px;
  border-color: #d8ebff;
  border-style: solid;
}

.scan-suite__corner--tl { top: 14px; left: 14px; border-width: 3px 0 0 3px; border-top-left-radius: 14px; }
.scan-suite__corner--tr { top: 14px; right: 14px; border-width: 3px 3px 0 0; border-top-right-radius: 14px; }
.scan-suite__corner--bl { bottom: 14px; left: 14px; border-width: 0 0 3px 3px; border-bottom-left-radius: 14px; }
.scan-suite__corner--br { bottom: 14px; right: 14px; border-width: 0 3px 3px 0; border-bottom-right-radius: 14px; }

.scan-suite__state,
.scan-suite__result {
  min-height: 420px;
  display: grid;
  align-content: center;
  justify-items: center;
  gap: 12px;
  text-align: center;
}

.scan-suite__result--success {
  background: linear-gradient(180deg, rgba(83, 129, 111, 0.22), rgba(10, 12, 18, 0));
}

.scan-suite__result--error {
  background: linear-gradient(180deg, rgba(153, 82, 82, 0.18), rgba(10, 12, 18, 0));
}

.scan-suite__side-card,
.scan-suite__side-block {
  display: grid;
  gap: 12px;
}

@keyframes scan-beam {
  0%, 100% { transform: translateY(0); opacity: 0.6; }
  50% { transform: translateY(224px); opacity: 1; }
}

@media (max-width: 980px) {
  .scan-suite__layout {
    grid-template-columns: 1fr;
  }
}
</style>
